import { useMemo, useState } from "react";
import { Search, Plus, CheckCircle2, XCircle } from "lucide-react";
import type { CVData } from "@/lib/cv-templates";

type Props = {
  cv: CVData | null;
  targetRole: string;
  initialKeywords?: string;
  onAddMissing?: (skills: string[]) => void;
};

const STOPWORDS = new Set([
  "the","and","with","for","you","your","our","that","this","from","have","has","are","was","were","will","into","over","under","using","via","per","etc","inc","ltd","plc","pty","role","roles","job","jobs","work","working",
]);

function tokenize(s: string): string[] {
  return (s || "")
    .toLowerCase()
    .split(/[^a-z0-9+#./-]+/g)
    .map((w) => w.trim())
    .filter((w) => w.length > 2 && !STOPWORDS.has(w));
}

export function KeywordChecker({ cv, targetRole, initialKeywords = "", onAddMissing }: Props) {
  const [raw, setRaw] = useState(initialKeywords);

  const keywords = useMemo(() => {
    const fromField = raw
      .split(/[,\n;]+/g)
      .map((k) => k.trim())
      .filter(Boolean);
    if (fromField.length) return fromField;
    // Fallback: derive simple keywords from target role
    return tokenize(targetRole).slice(0, 6);
  }, [raw, targetRole]);

  const haystack = useMemo(() => {
    if (!cv) return "";
    const bullets = (cv.experience || []).flatMap((e) => e.bullets || []).join(" ");
    return `${cv.headline || ""} ${cv.summary || ""} ${(cv.skills || []).join(" ")} ${bullets}`.toLowerCase();
  }, [cv]);

  const { present, missing } = useMemo(() => {
    const p: string[] = [];
    const m: string[] = [];
    for (const k of keywords) {
      const needle = k.toLowerCase();
      if (haystack.includes(needle)) p.push(k);
      else m.push(k);
    }
    return { present: p, missing: m };
  }, [keywords, haystack]);

  const coverage = keywords.length
    ? Math.round((present.length / keywords.length) * 100)
    : 0;

  return (
    <section className="rounded-lg border border-border bg-card p-5" aria-labelledby="kw-heading">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 id="kw-heading" className="flex items-center gap-2 text-sm font-semibold text-navy">
          <Search className="h-4 w-4" />
          ATS keyword checker
        </h3>
        {keywords.length > 0 && (
          <span className="rounded-full border border-input bg-background px-3 py-1 text-xs font-semibold text-navy">
            {present.length}/{keywords.length} matched · {coverage}%
          </span>
        )}
      </div>

      <p className="mt-2 text-xs text-foreground/70">
        Paste the job-ad keywords (or the must-have skills you want to rank for) — comma separated.
        We'll highlight what's missing from your CV so you can add it before downloading.
      </p>

      <textarea
        rows={2}
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder="e.g. POPIA, FICA, AML, risk assessment, stakeholder management, MS 365"
        className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
      />

      {keywords.length === 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          Add keywords above (or set a Target role) to start scoring.
        </p>
      )}

      {keywords.length > 0 && (
        <>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full transition-all ${coverage >= 70 ? "bg-emerald-500" : coverage >= 40 ? "bg-amber-500" : "bg-red-500"}`}
              style={{ width: `${coverage}%` }}
            />
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Present in CV ({present.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {present.length === 0 && <span className="text-xs text-muted-foreground">None yet.</span>}
                {present.map((k) => (
                  <span key={k} className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-800">
                    {k}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-amber-700">
                <XCircle className="h-3.5 w-3.5" /> Missing — add these ({missing.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {missing.length === 0 && <span className="text-xs text-emerald-700">All keywords covered ✓</span>}
                {missing.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => onAddMissing?.([k])}
                    className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-900 hover:bg-amber-100"
                    title="Add to skills"
                  >
                    <Plus className="h-2.5 w-2.5" /> {k}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {missing.length > 0 && onAddMissing && (
            <button
              type="button"
              onClick={() => onAddMissing(missing)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-navy bg-background px-3 py-1.5 text-xs font-medium text-navy hover:bg-navy/5"
            >
              <Plus className="h-3.5 w-3.5" /> Add all missing keywords to Skills
            </button>
          )}
        </>
      )}
    </section>
  );
}
